/**
 * Capacitor Shim
 *
 * Exposes the same `window.electron` API surface as `web-electron-shim.js`,
 * but routes:
 *   - storage to @capacitor/preferences (Keychain on iOS, encrypted prefs on Android)
 *   - OAuth callbacks to @capacitor/app deep links (custom URL scheme mixgoat://)
 *   - in-app purchases to RevenueCat
 *   - tier checks to /api/tier/reconciled (so Apple/Google entitlements count)
 *
 * Loaded ONLY when window.Capacitor.isNativePlatform() === true.
 * Loaded via the inline script in public/index.html instead of web-electron-shim.js.
 *
 * IMPORTANT: every method exposed by web-electron-shim.js must exist here with
 * a matching signature, even if implementation is a no-op. The shim parity test
 * (see plan) asserts surface identity.
 */
import { resolveAppleArtworkOnly, appleArtworkOnlyNow, stripNonAppleArtwork } from './shared/appleArtwork';

(function () {
  // Self-guard: only run under Capacitor on a real native device. On web and
  // Electron, this file is imported but the entire IIFE returns immediately,
  // making it a true no-op — zero regression surface on existing platforms.
  const isNative = typeof window !== 'undefined'
    && window.Capacitor
    && typeof window.Capacitor.isNativePlatform === 'function'
    && window.Capacitor.isNativePlatform();
  if (!isNative) return;
  if (window.electron && window.electron.db) return; // real Electron — never on native, but guard anyway

  const nativePlatform = () =>
    (window.Capacitor && window.Capacitor.getPlatform && window.Capacitor.getPlatform()) || 'native';

  // Route every request through app.mixgoat.com, which is the same
  // Cloudflare Pages site that serves the web app and proxies /api/* to
  // each backend service (auth, subscriptions, library, djpal,
  // error-reporting). Using one origin = one CORS allowlist entry to
  // maintain per service, and behavior matches the web flow exactly.
  const API_HOST = 'https://app.mixgoat.com';
  // /api/tier/* is not routed via app.mixgoat.com (Cloudflare gap) — call the
  // subscription service directly. All other paths go through app.mixgoat.com.
  const TIER_RECONCILED_PATH = 'https://subscriptions.mixgoat.com/api/tier/reconciled';
  // GET /api/auth/google must hit auth.mixgoat.com directly: the nginx config
  // in front of app.mixgoat.com routes POST /api/auth/* to auth-service but
  // GETs fall through to the SPA's index.html (200 HTML instead of the 302
  // → accounts.google.com). Browser plugin then loads the React app inside
  // the OAuth tab, producing "405" on Android and a hung tab on iOS.
  // POST /api/auth/google/ticket is reachable via app.mixgoat.com.
  const AUTH_HOST = 'https://auth.mixgoat.com';

  function resolveHost(/* path */) {
    return API_HOST;
  }

  // ── Persistent storage (Capacitor Preferences) ──
  // Synchronous reads via in-memory mirror; async writes back to native store.
  const memStore = {};
  let prefsReady = false;
  async function hydratePrefs() {
    try {
      const { Preferences } = window.Capacitor.Plugins;
      const { keys } = await Preferences.keys();
      for (const k of keys) {
        const { value } = await Preferences.get({ key: k });
        memStore[k] = value;
      }
    } catch (err) {
      console.warn('[capacitor-shim] Preferences hydrate failed', err);
    }
    prefsReady = true;
  }
  function prefsSet(key, val) {
    memStore[key] = val;
    try { window.Capacitor.Plugins.Preferences.set({ key, value: val }); } catch (_) {}
  }
  function prefsRemove(key) {
    delete memStore[key];
    try { window.Capacitor.Plugins.Preferences.remove({ key }); } catch (_) {}
  }

  // ── Auth token cache (mirrors web-electron-shim's LS pattern) ──
  const LS_ACCESS = 'cap-shim:access-token';
  const LS_REFRESH = 'cap-shim:refresh-token';
  const LS_USER = 'cap-shim:user';

  let cachedAccessToken = null;
  let cachedRefreshToken = null;

  // localStorage mirror keys — authThunk.checkAuthStatus's IS_LOCAL_DEV
  // branch (which fires on the capacitor://localhost origin too) reads
  // these synchronously on launch to restore the session before any
  // async work runs. Without this mirror, users would land on the login
  // screen every cold start, even though valid tokens exist in Keychain.
  const WEB_LS_ACCESS = 'web-shim:dev-access-token';
  const WEB_LS_REFRESH = 'web-shim:dev-refresh-token';

  // Per-install identity for the auth service's device list. localStorage is
  // enough here: it is mirrored from Keychain-backed prefs on cold start, and
  // a lost id only costs one "new device" notice.
  const DEVICE_ID_KEY = 'mg:device-id';
  function nativeDeviceInfo() {
    let id = null;
    try { id = localStorage.getItem(DEVICE_ID_KEY); } catch (_) { /* ignore */ }
    if (!id) {
      id = (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : `native-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      try { localStorage.setItem(DEVICE_ID_KEY, id); } catch (_) { /* ignore */ }
    }
    const platform = (window.Capacitor && window.Capacitor.getPlatform && window.Capacitor.getPlatform()) || 'native';
    const name = platform === 'ios' ? (/iPad/.test(navigator.userAgent || '') ? 'iPad' : 'iPhone') : platform === 'android' ? 'Android phone' : 'Mobile device';
    return { id, name, platform };
  }
  const WEB_LS_USER = 'web-shim:dev-user';

  function rememberTokenFromResponse(data) {
    if (!data) return;
    if (data.accessToken) {
      cachedAccessToken = data.accessToken;
      prefsSet(LS_ACCESS, data.accessToken);
      try { localStorage.setItem(WEB_LS_ACCESS, data.accessToken); } catch {}
    }
    if (data.refreshToken) {
      cachedRefreshToken = data.refreshToken;
      prefsSet(LS_REFRESH, data.refreshToken);
      try { localStorage.setItem(WEB_LS_REFRESH, data.refreshToken); } catch {}
    }
    if (data.user && typeof data.user === 'object') {
      const userJson = JSON.stringify(data.user);
      prefsSet(LS_USER, userJson);
      try { localStorage.setItem(WEB_LS_USER, userJson); } catch {}
    }
  }

  function clearCachedTokens() {
    cachedAccessToken = null;
    cachedRefreshToken = null;
    prefsRemove(LS_ACCESS);
    prefsRemove(LS_REFRESH);
    prefsRemove(LS_USER);
    try {
      localStorage.removeItem(WEB_LS_ACCESS);
      localStorage.removeItem(WEB_LS_REFRESH);
      localStorage.removeItem(WEB_LS_USER);
    } catch {}
  }

  function authHeaderToken(explicit) {
    // Fallback order: explicit arg → shim's own cached token (native login flow)
    // → React auth flow token (stored under 'auth:accessToken' by authThunk)
    // → the shim's localStorage mirror (WEB_LS_ACCESS). The mirror is the
    // cold-start safety net: cachedAccessToken is only restored after the
    // async hydratePrefs() round-trips the native bridge, so requests fired
    // by tabs mounting at launch (e.g. the djpal token exchange) would
    // otherwise go out with NO Authorization header → server 401 "Access
    // token required" (App Review rejection 2026-06). The mirror is written
    // synchronously on every auth response and survives relaunch.
    try {
      return explicit || cachedAccessToken
        || localStorage.getItem('auth:accessToken')
        || localStorage.getItem(WEB_LS_ACCESS)
        || null;
    } catch { return explicit || cachedAccessToken || null; }
  }
  function refreshTokenForBody(explicit) {
    // Same cold-start rationale as authHeaderToken: without the mirror
    // fallback, a refresh fired before Keychain hydration (e.g. the cached
    // access token is >15 min old at launch) sends refreshToken: undefined
    // and the whole chain fails even though a valid refresh token exists.
    if (explicit || cachedRefreshToken) return explicit || cachedRefreshToken;
    try { return localStorage.getItem(WEB_LS_REFRESH) || undefined; } catch { return undefined; }
  }

  // Apple-only artwork (src/shared/appleArtwork.js): on by default here (iOS + Android
  // compliance), REACT_APP_APPLE_ARTWORK_ONLY=0 at build time turns it off.
  // The live server mode (utils/appleArtworkMode.js polls it) wins once known.
  const APPLE_ARTWORK_BUILD = resolveAppleArtworkOnly(process.env.REACT_APP_APPLE_ARTWORK_ONLY, true);
  const appleArtworkOnly = () => appleArtworkOnlyNow(true, APPLE_ARTWORK_BUILD);

  async function apiFetch(method, url, body, token) {
    const headers = body ? { 'Content-Type': 'application/json' } : {};
    // NOTE: do NOT add custom request headers here. The edge CORS allowlist
    // only permits Content-Type + Authorization, so any extra header (e.g. a
    // platform tag) fails the WebView preflight → "Load failed" on every call.
    // The signup platform signal travels in the request body/query instead
    // (see auth.register / appleLogin / googleLogin).
    const t = authHeaderToken(token);
    if (t) headers['Authorization'] = `Bearer ${t}`;
    const full = url.startsWith('http') ? url : resolveHost(url) + url;
    const init = { method, headers };
    if (body !== undefined) init.body = JSON.stringify(body);
    let res;
    try {
      res = await fetch(full, init);
    } catch (netErr) {
      return { error: netErr.message || 'Network error', status: 0 };
    }
    if (res.status === 204) return { success: true };
    const text = await res.text();
    let data = {};
    try { data = text ? JSON.parse(text) : {}; } catch (_) { data = { error: text }; }
    if (!res.ok) {
      return { error: data.error || data.message || `Request failed (${res.status})`, status: res.status };
    }
    rememberTokenFromResponse(data);
    if (appleArtworkOnly()) { try { stripNonAppleArtwork(data); } catch (_) { /* never break a response over cover filtering */ } }
    return data;
  }
  const apiGet = (url, token) => apiFetch('GET', url, undefined, token);
  const apiPost = (url, body, token) => apiFetch('POST', url, body, token);
  const apiPut = (url, body, token) => apiFetch('PUT', url, body, token);
  const apiPatch = (url, body, token) => apiFetch('PATCH', url, body, token);
  const apiDelete = (url, token, body) => apiFetch('DELETE', url, body, token);

  // ── Trial info (same logic as web shim) ──
  function getTrialInfo(user) {
    const s = user?.trial_started_at;
    const e = user?.trial_ends_at;
    if (!s || !e) return { trialStartDate: null, trialEndDate: null, daysRemaining: 0, isTrialActive: false, isTrialExpired: false };
    const trialStart = new Date(s);
    const trialEnd = new Date(e);
    const msRemaining = trialEnd.getTime() - Date.now();
    const daysRemaining = Math.max(0, Math.ceil(msRemaining / 86400000));
    return {
      trialStartDate: trialStart.toISOString(),
      trialEndDate: trialEnd.toISOString(),
      daysRemaining,
      isTrialActive: msRemaining > 0,
      isTrialExpired: msRemaining <= 0,
    };
  }

  // ── OAuth via @capacitor/browser + deep link callback ──
  // mixgoat:// scheme is registered in ios-app Info.plist and android-app intent-filter.
  // Flow mirrors the existing desktop ticket-exchange path:
  //   1. Open Browser to /api/auth/google (server's default redirect is
  //      mixgoat://auth/callback when no web_redirect is provided).
  //   2. Listen for the deep link mixgoat://auth/callback?ticket=...
  //   3. POST the ticket to /api/auth/google/ticket → receive {accessToken, refreshToken, user}.
  // Module-scoped handle to the in-flight googleLogin's finish() callback so
  // that cancelGoogleLogin can resolve the promise directly. iOS
  // SFSafariViewController's `dismiss(animated:)` doesn't reliably fire the
  // `browserFinished` Capacitor event, so we can't rely on the event-based
  // cancel path on iOS — the Cancel button has to force-resolve.
  let pendingGoogleAuthFinish = null;
  // Same mechanism for the Apple Android web flow (iOS uses the native sheet,
  // which has its own cancel — no finish handle needed there).
  let pendingAppleAuthFinish = null;

  async function googleLogin() {
    try {
      const { Browser, App } = window.Capacitor.Plugins;
      // platform=ios/android rides through the OAuth state so the callback
      // skips the server auto-trial for native signups (this Browser request
      // can't carry the X-Client-Platform header apiFetch adds).
      const plat = (window.Capacitor && window.Capacitor.getPlatform && window.Capacitor.getPlatform()) || '';
      const authUrl = `${AUTH_HOST}/api/auth/google${plat ? `?platform=${encodeURIComponent(plat)}` : ''}`;
      const result = await new Promise((resolve) => {
        let settled = false;
        let urlHandlePromise = null;
        let finishedHandlePromise = null;
        let timeoutId = null;
        // browserFinished can fire BEFORE appUrlOpen on Android when the
        // OS hands the mixgoat:// deep link off to our app and auto-closes
        // the Custom Tab. Defer the cancel resolve so a deep link arriving
        // within this window cancels the timer.
        //
        // This window MUST comfortably exceed the Android
        // onNewIntent → Capacitor bridge → JS `appUrlOpen` latency. At 500ms
        // testers were intermittently bounced back to the auth screen after a
        // successful Google sign-in: the Custom Tab auto-closed (→
        // browserFinished), the 500ms timer expired, and the promise resolved
        // `{error:'cancelled'}` (silently swallowed by the thunk) BEFORE the
        // ticket-bearing appUrlOpen arrived. 2500ms gives the deep link room
        // to win on slow devices / cold bridges. A genuine user-cancel (tab
        // swipe-away) now takes 2.5s to register, but the explicit in-app
        // Cancel button (cancelGoogleLogin) still cancels instantly, so the
        // only cost is the rare swipe-to-dismiss case.
        const BROWSER_FINISHED_GRACE_MS = 2500;
        // Hard ceiling: if neither a deep link nor an event arrives within
        // this window, give up. Catches iOS edge cases (SFSafariViewController
        // dismissals that don't emit browserFinished) so the spinner can
        // never hang forever. 90s is long enough for slow OAuth (incl.
        // 2FA), short enough that a stuck spinner isn't a forever bug.
        const HARD_TIMEOUT_MS = 90 * 1000;
        let cancelTimer = null;

        const cleanup = async () => {
          if (cancelTimer) { clearTimeout(cancelTimer); cancelTimer = null; }
          if (timeoutId) { clearTimeout(timeoutId); timeoutId = null; }
          try { (await urlHandlePromise)?.remove(); } catch (_) {}
          try { (await finishedHandlePromise)?.remove(); } catch (_) {}
        };
        const finish = (value) => {
          if (settled) return;
          settled = true;
          pendingGoogleAuthFinish = null;
          // Fire-and-forget — caller doesn't need to wait on listener teardown.
          cleanup();
          resolve(value);
        };
        pendingGoogleAuthFinish = finish;

        urlHandlePromise = App.addListener('appUrlOpen', async (event) => {
          if (!event || !event.url || !event.url.startsWith('mixgoat://auth/callback')) return;
          // Deep link arrived — disarm the pending browserFinished cancel.
          if (cancelTimer) { clearTimeout(cancelTimer); cancelTimer = null; }
          try { Browser.close(); } catch (_) {}
          const url = new URL(event.url);
          const ticket = url.searchParams.get('ticket');
          const errParam = url.searchParams.get('error');
          if (errParam) return finish({ error: errParam });
          if (!ticket) return finish({ error: 'No ticket in callback' });
          const tokenRes = await apiPost('/api/auth/google/ticket', { ticket });
          if (tokenRes && !tokenRes.error) {
            finish({ success: true, ...tokenRes });
          } else {
            finish({ error: tokenRes?.error || 'Ticket exchange failed' });
          }
        });

        // Browser closed without a deep link arriving. Reliable on Android
        // (Custom Tab back/swipe), unreliable on iOS SFSafariViewController
        // — for iOS, the in-app Cancel button (cancelGoogleLogin →
        // pendingGoogleAuthFinish) and the hard timeout below are the
        // real safety nets.
        finishedHandlePromise = Browser.addListener('browserFinished', () => {
          if (cancelTimer || settled) return;
          cancelTimer = setTimeout(() => {
            cancelTimer = null;
            finish({ error: 'cancelled' });
          }, BROWSER_FINISHED_GRACE_MS);
        });

        timeoutId = setTimeout(() => {
          timeoutId = null;
          finish({ error: 'Authentication timed out. Please try again.' });
        }, HARD_TIMEOUT_MS);

        Browser.open({ url: authUrl, presentationStyle: 'fullscreen' });
      });
      return result;
    } catch (err) {
      return { error: err?.message || 'OAuth failed' };
    }
  }

  // ── RevenueCat IAP wrapper ──
  // configure() runs ONCE at shim init with the platform-specific public
  // SDK key (App Store key on iOS, Play Store key on Android). Without
  // this, every call into Purchases.* fails with
  // "RevenueCat singleton not yet configured" and the paywall shows
  // "Offerings unavailable". Keys are CRA env vars (compiled into the
  // bundle at build time) — they are public-by-design and safe to ship.
  // logIn(userId) is called from App.js after auth so RevenueCat
  // attributes the entitlement to the MixGoat user_id (which the server
  // webhook reconciles back to tier:reconciled). Without it, purchases
  // are anonymous and the server can't credit the right account.
  let purchasesConfigured = false;
  async function ensurePurchasesConfigured() {
    if (purchasesConfigured) return true;
    const Purchases = window.Capacitor?.Plugins?.Purchases;
    if (!Purchases) return false;
    const platform = window.Capacitor?.getPlatform?.() || 'web';
    const apiKey = platform === 'ios'
      ? process.env.REACT_APP_REVENUECAT_IOS_KEY
      : process.env.REACT_APP_REVENUECAT_ANDROID_KEY;
    if (!apiKey) {
      console.warn('[capacitor-shim] RevenueCat API key missing for platform', platform);
      return false;
    }
    try {
      await Purchases.configure({ apiKey });
      purchasesConfigured = true;
      return true;
    } catch (err) {
      console.warn('[capacitor-shim] Purchases.configure failed', err);
      return false;
    }
  }

  const iap = {
    available: () => !!(window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins.Purchases),
    async logIn(userId) {
      if (!userId) return { error: 'Missing userId' };
      const ok = await ensurePurchasesConfigured();
      if (!ok) return { error: 'Purchases not configured' };
      try {
        const { Purchases } = window.Capacitor.Plugins;
        return await Purchases.logIn({ appUserID: String(userId) });
      } catch (err) { return { error: err?.message || 'logIn failed' }; }
    },
    async logOut() {
      if (!purchasesConfigured) return { success: true };
      try {
        const { Purchases } = window.Capacitor.Plugins;
        return await Purchases.logOut();
      } catch (err) { return { error: err?.message || 'logOut failed' }; }
    },
    async getOfferings() {
      const ok = await ensurePurchasesConfigured();
      if (!ok) return { error: 'Purchases not configured' };
      try {
        const { Purchases } = window.Capacitor.Plugins;
        return await Purchases.getOfferings();
      } catch (err) { return { error: err?.message || 'Offerings unavailable' }; }
    },
    async purchase(aPackage) {
      // RC Capacitor v9+ requires a full Package object, not a product ID
      // string. Earlier v8 took `{productIdentifier}` — that signature was
      // removed and now throws "missing product parameter" if used.
      // MobilePaywall passes offerings.current.monthly / .annual directly.
      const ok = await ensurePurchasesConfigured();
      if (!ok) return { error: 'Purchases not configured' };
      if (!aPackage || typeof aPackage !== 'object') {
        return { error: 'Missing package object' };
      }
      try {
        const { Purchases } = window.Capacitor.Plugins;
        const res = await Purchases.purchasePackage({ aPackage });
        return { success: true, ...res };
      } catch (err) {
        if (err?.userCancelled || /cancel/i.test(err?.message || '')) {
          return { cancelled: true };
        }
        return { error: err?.message || 'Purchase failed' };
      }
    },
    async restorePurchases() {
      const ok = await ensurePurchasesConfigured();
      if (!ok) return { error: 'Purchases not configured' };
      try {
        const { Purchases } = window.Capacitor.Plugins;
        const res = await Purchases.restorePurchases();
        return { success: true, ...res };
      } catch (err) { return { error: err?.message || 'Restore failed' }; }
    },
    async getCustomerInfo() {
      const ok = await ensurePurchasesConfigured();
      if (!ok) return { error: 'Purchases not configured' };
      try {
        const { Purchases } = window.Capacitor.Plugins;
        const res = await Purchases.getCustomerInfo();
        return res;
      } catch (err) { return { error: err?.message || 'CustomerInfo unavailable' }; }
    },
  };

  // ── Shim ──
  window.electron = {
    // Native audio plugin handle — NativePlaybackEngine.js consumes this.
    // Exposed at the top level (not under .audio) to keep the native player
    // decoupled from the existing window.electron.* IPC surface, which is
    // intentionally web/Electron-only.
    // Plugin lookup is intentionally LAZY via a getter. Capacitor registers
    // App-target plugins in MixGoatBridgeViewController.capacitorDidLoad(),
    // which fires AFTER this shim's IIFE evaluates. An eager lookup here
    // captures `null` and freezes nativeAudio as unavailable for the
    // lifetime of the page, which broke the hybrid audio path (silent
    // bug-class: "lock-screen pause + unlock = music keeps playing"
    // because the hybrid never engaged and iOS auto-handled HTML5 audio).
    // The getter re-resolves on every access, so once the plugin
    // registers, all subsequent reads pick it up.
    get nativeAudio() {
      const plugins = window.Capacitor && window.Capacitor.Plugins;
      return plugins ? plugins.MixGoatAudio : null;
    },

    // ── Auth ──
    auth: {
      // Eviction recovery. iOS can purge WKWebView localStorage (the token
      // mirror) under memory pressure during heavy foreground churn — Sign in
      // with Apple's SFSafariViewController + the StoreKit purchase sheet are
      // exactly that. If that happens AND the WebView reloads (Redux resets to
      // empty on native), the app can be `isAuthenticated` with no readable
      // token, and the For You connect goes out tokenless → "Access token
      // required" (the App Review rejection). The Keychain (Preferences) is NOT
      // evicted, so re-hydrate from it and re-mirror into localStorage + the
      // in-memory cache. Returns the recovered tokens so the caller can also
      // repopulate Redux.
      recoverSession: async () => {
        try { await hydratePrefs(); } catch (_) {}
        cachedAccessToken = memStore[LS_ACCESS] || cachedAccessToken || null;
        cachedRefreshToken = memStore[LS_REFRESH] || cachedRefreshToken || null;
        try {
          if (memStore[LS_ACCESS]) localStorage.setItem(WEB_LS_ACCESS, memStore[LS_ACCESS]);
          if (memStore[LS_REFRESH]) localStorage.setItem(WEB_LS_REFRESH, memStore[LS_REFRESH]);
          if (memStore[LS_USER]) localStorage.setItem(WEB_LS_USER, memStore[LS_USER]);
        } catch (_) {}
        let user = null;
        try { user = memStore[LS_USER] ? JSON.parse(memStore[LS_USER]) : null; } catch (_) {}
        return { accessToken: cachedAccessToken, refreshToken: cachedRefreshToken, user };
      },
      // The device id lets the account's device list name this phone and
      // sends "new sign-in" notices; the email-code step (supportsChallenge)
      // is deliberately NOT opted into on native until it is exercised on a
      // device — an unsupported 202 here would look like a hung sign-in.
      login: (email, password) => apiPost('/api/auth/login', { email, password, device: nativeDeviceInfo() }),
      loginVerify: (challengeId, code) => apiPost('/api/auth/login/verify', { challengeId, code, device: nativeDeviceInfo() }),
      loginResend: (challengeId) => apiPost('/api/auth/login/resend', { challengeId }),
      listSessions: (accessToken) => apiGet('/api/auth/sessions', accessToken),
      revokeSession: (accessToken, sessionId) => apiPost(`/api/auth/sessions/${encodeURIComponent(sessionId)}/revoke`, {}, accessToken),
      revokeOtherSessions: (accessToken) => apiPost('/api/auth/sessions/revoke-others', {}, accessToken),
      // platform travels in the body (not a header) so it survives the edge
      // CORS allowlist; the server skips the auto-trial for ios/android signups.
      register: (data) => apiPost('/api/auth/register', { ...data, platform: (window.Capacitor && window.Capacitor.getPlatform && window.Capacitor.getPlatform()) || undefined }),
      logout: (refreshToken) => {
        const r = apiPost('/api/auth/logout', { refreshToken: refreshTokenForBody(refreshToken) });
        clearCachedTokens();
        return r;
      },
      refreshToken: (refreshToken) => apiPost('/api/auth/refresh', { refreshToken: refreshTokenForBody(refreshToken) }),
      getProfile: (accessToken) => apiGet('/api/auth/me', accessToken),
      // In-app account deletion (App Review 5.1.1(v)). payload = { password }
      // for password accounts; OAuth-only accounts pass {} (server confirms via
      // the authenticated session). Server cancels Stripe + purges library data.
      deleteAccount: (accessToken, payload) => apiPost('/api/auth/delete-account', payload || {}, accessToken),
      verifyEmail: (token) => apiGet(`/api/auth/verify-email?token=${encodeURIComponent(token)}`),
      forgotPassword: (email) => apiPost('/api/auth/forgot-password', { email }),
      resetPassword: (data) => apiPost('/api/auth/reset-password', data),
      googleLogin,
      cancelGoogleLogin: () => {
        try { window.Capacitor.Plugins.Browser.close(); } catch (e) {
        }
        // Force-resolve the in-flight googleLogin promise rather than
        // waiting for a Browser event that may never fire (iOS swipe-down
        // dismissal of SFSafariViewController in particular).
        if (pendingGoogleAuthFinish) {
          try {
            pendingGoogleAuthFinish({ error: 'cancelled' });
          } catch (e) {
          }
        } else {
        }
        return Promise.resolve();
      },
      cancelAppleLogin: () => {
        // Closes the in-app browser. iOS uses the native sheet which has its
        // own cancel button, so this mainly matters for the Android web flow.
        try { window.Capacitor.Plugins.Browser.close(); } catch (_) {}
        // Force-resolve the in-flight Apple promise (Android web flow) rather
        // than waiting for a Browser event that may never fire — mirrors
        // cancelGoogleLogin.
        if (pendingAppleAuthFinish) {
          try { pendingAppleAuthFinish({ error: 'cancelled' }); } catch (_) {}
        }
        return Promise.resolve();
      },
      // Sign in with Apple. Mandatory under App Store Review 4.8 because we
      // offer Google sign-in. On iOS we use the native ASAuthorizationApple
      // sheet (no browser bounce, Face ID); on Android (no native Apple
      // plugin available) we fall back to the web OAuth flow via
      // @capacitor/browser + mixgoat:// deep link, same pattern as Google.
      appleLogin: async () => {
        const platform = window.Capacitor?.getPlatform?.() || 'web';

        // iOS — native plugin path
        if (platform === 'ios') {
          try {
            const { SignInWithApple } = window.Capacitor.Plugins;
            if (!SignInWithApple) return { error: 'Apple sign-in unavailable' };
            const res = await SignInWithApple.authorize({
              clientId: 'com.mixgoat.app',
              redirectURI: 'mixgoat://auth/callback',
              scopes: 'email name',
            });
            const r = res?.response || {};
            if (!r.identityToken) return { error: 'No identity token returned' };
            return apiPost('/api/auth/apple', {
              identityToken: r.identityToken,
              platform: 'ios',
              user: {
                name: {
                  firstName: r.givenName || null,
                  lastName: r.familyName || null,
                },
                email: r.email || null,
              },
            });
          } catch (err) {
            if (err?.userCancelled || /cancel/i.test(err?.message || '')) {
              return { error: 'cancelled' };
            }
            return { error: err?.message || 'Apple sign-in failed' };
          }
        }

        // Android / other Capacitor platforms — web OAuth via browser deep link.
        // Mirrors the googleLogin robustness: a deep link that arrives shortly
        // after the Custom Tab auto-closes must win the race against the
        // browserFinished "cancel" (see BROWSER_FINISHED_GRACE_MS rationale in
        // googleLogin), and a hard timeout + force-resolvable cancel guarantee
        // the spinner can never hang forever (the original Apple-Android flow
        // had neither, so a closed tab or missed event hung indefinitely).
        try {
          const { Browser, App } = window.Capacitor.Plugins;
          const authUrl = `${API_HOST}/api/auth/apple/web`;
          const result = await new Promise((resolve) => {
            let settled = false;
            let urlHandlePromise = null;
            let finishedHandlePromise = null;
            let cancelTimer = null;
            let timeoutId = null;
            const BROWSER_FINISHED_GRACE_MS = 2500;
            const HARD_TIMEOUT_MS = 90 * 1000;

            const cleanup = async () => {
              if (cancelTimer) { clearTimeout(cancelTimer); cancelTimer = null; }
              if (timeoutId) { clearTimeout(timeoutId); timeoutId = null; }
              try { (await urlHandlePromise)?.remove(); } catch (_) {}
              try { (await finishedHandlePromise)?.remove(); } catch (_) {}
            };
            const finish = (value) => {
              if (settled) return;
              settled = true;
              pendingAppleAuthFinish = null;
              cleanup();
              resolve(value);
            };
            pendingAppleAuthFinish = finish;

            urlHandlePromise = App.addListener('appUrlOpen', async (event) => {
              if (!event || !event.url || !event.url.startsWith('mixgoat://auth/callback')) return;
              if (cancelTimer) { clearTimeout(cancelTimer); cancelTimer = null; }
              try { Browser.close(); } catch (_) {}
              const url = new URL(event.url);
              const ticket = url.searchParams.get('ticket');
              const errParam = url.searchParams.get('error');
              if (errParam) return finish({ error: errParam });
              if (!ticket) return finish({ error: 'No ticket in callback' });
              const tokenRes = await apiPost('/api/auth/apple/ticket', { ticket });
              if (tokenRes && !tokenRes.error) {
                finish({ success: true, ...tokenRes });
              } else {
                finish({ error: tokenRes?.error || 'Ticket exchange failed' });
              }
            });

            finishedHandlePromise = Browser.addListener('browserFinished', () => {
              if (cancelTimer || settled) return;
              cancelTimer = setTimeout(() => {
                cancelTimer = null;
                finish({ error: 'cancelled' });
              }, BROWSER_FINISHED_GRACE_MS);
            });

            timeoutId = setTimeout(() => {
              timeoutId = null;
              finish({ error: 'Authentication timed out. Please try again.' });
            }, HARD_TIMEOUT_MS);

            Browser.open({ url: authUrl, presentationStyle: 'fullscreen' });
          });
          return result;
        } catch (err) {
          return { error: err?.message || 'Apple sign-in failed' };
        }
      },
      manageSubscription: (accessToken) => apiGet('/api/subscriptions/current', accessToken),
    },

    // ── Library ──
    library: {
      getLikes: (token, page, limit) => apiGet(`/api/library/likes?page=${page}&limit=${limit}&type=track`, token),
      likeTrack: (token, track) => apiPost('/api/library/likes', track, token),
      unlikeTrack: (token, artist, title) => apiPost('/api/library/likes/unlike', { artist, title }, token),
      checkLikes: (token, tracks) => apiPost('/api/library/likes/check', { tracks, withNotify: true }, token),
      setTrackNotify: (token, artist, title, notify) => apiPost('/api/library/likes/notify', { artist, title, notify }, token),
      getLikedMixes: (token, page, limit) => apiGet(`/api/library/likes?page=${page}&limit=${limit}&type=mix`, token),
      likeMix: (token, mix) => apiPost('/api/library/likes', { ...mix, type: 'mix' }, token),
      unlikeMix: (token, title, djName) => apiPost('/api/library/likes/unlike', { artist: djName, title, type: 'mix' }, token),
      checkMixLikes: (token, mixes) => apiPost('/api/library/likes/check', { tracks: mixes, type: 'mix' }, token),
      getFollowedArtists: (token, page, limit) => apiGet(`/api/library/artists?page=${page}&limit=${limit}`, token),
      followArtist: (token, artist) => apiPost('/api/library/artists', artist, token),
      unfollowArtist: (token, artistName) => apiPost('/api/library/artists/unfollow', { artistName }, token),
      checkFollowedArtists: (token, artistNames) => apiPost('/api/library/artists/check', { artistNames, withNotify: true }, token),
      setArtistNotify: (token, artistName, notify) => apiPost('/api/library/artists/notify', { artistName, notify }, token),
      // Label follows
      getFollowedLabels: (token, page, limit) => apiGet(`/api/library/labels?page=${page}&limit=${limit}`, token),
      followLabel: (token, label) => apiPost('/api/library/labels', label, token),
      unfollowLabel: (token, labelPublicId) => apiPost('/api/library/labels/unfollow', { labelPublicId }, token),
      checkFollowedLabels: (token, labelPublicIds) => apiPost('/api/library/labels/check', { labelPublicIds }, token),
      setLabelNotify: (token, labelPublicId, notify) => apiPost('/api/library/labels/notify', { labelPublicId, notify }, token),
      getNotifications: (token, page, limit) => apiGet(`/api/library/notifications?page=${page}&limit=${limit}`, token),
      getNotificationUnreadCount: (token) => apiGet('/api/library/notifications/unread-count', token),
      getNotificationSettings: (token) => apiGet('/api/library/notifications/settings', token),
      updateNotificationSettings: (token, settings) => apiPut('/api/library/notifications/settings', settings, token),
      getPreferences: (token) => apiGet('/api/library/preferences', token),
      updatePreferences: (token, prefs) => apiPatch('/api/library/preferences', prefs, token),
      markNotificationRead: (token, notificationId) => apiPost('/api/library/notifications/read', { notificationId }, token),
      markAllNotificationsRead: (token) => apiPost('/api/library/notifications/read-all', {}, token),
      deleteNotification: (token, notificationId) => apiPost('/api/library/notifications/delete', { notificationId }, token),
      deleteAllNotifications: (token) => apiPost('/api/library/notifications/delete-all', {}, token),
      // Server-side DJ library (synced up by the desktop app) — mobile/web's
      // source for "Artists from your library" (no local SQLite here).
      getDJLibraryArtists: (token, limit) => apiGet(`/api/library/dj-library/artists?limit=${limit || 200}`, token),
      getPlaylists: (token) => apiGet('/api/library/playlists', token),
      searchPlaylistsByTrack: (token, q) => apiGet('/api/library/playlists/search-by-track?q=' + encodeURIComponent(q), token),
      getPlaylist: (token, id) => apiGet(`/api/library/playlists/${id}`, token),
      createPlaylist: (token, data) => apiPost('/api/library/playlists', data, token),
      updatePlaylist: (token, id, data) => apiPut(`/api/library/playlists/${id}`, data, token),
      deletePlaylist: (token, id) => apiDelete(`/api/library/playlists/${id}`, token),
      addTrackToPlaylist: (token, playlistId, track) => apiPost(`/api/library/playlists/${playlistId}/tracks`, track, token),
      // rowId = playlist-track ROW id, not the catalog track_id (desktop-only
      // backfill path in practice; defined for API parity)
      setPlaylistTrackFilePath: (token, playlistId, rowId, filePath) => apiPatch(`/api/library/playlists/${playlistId}/tracks/${rowId}/file-path`, { file_path: filePath }, token),
      removeTrackFromPlaylist: (token, playlistId, trackId) => apiDelete(`/api/library/playlists/${playlistId}/tracks/${trackId}`, token),
      // By ROW PK — the only removal path for local-file/mix rows (track_id NULL).
      removePlaylistRow: (token, playlistId, rowId) => apiDelete(`/api/library/playlists/${playlistId}/tracks/rows/${rowId}`, token),
      // rowIds = user_playlist_tracks.id (the `id` on each row the playlist GET returns)
      reorderPlaylistTracks: (token, playlistId, rowIds) => apiPut(`/api/library/playlists/${playlistId}/tracks/reorder`, { rowIds }, token),
      getPlannerSets: (token) => apiGet('/api/library/planner-sets', token),
      savePlannerSet: (token, setId, data) => apiPut(`/api/library/planner-sets/${setId}`, data, token),
      deletePlannerSet: (token, setId) => apiDelete(`/api/library/planner-sets/${setId}`, token),
      getSearchHistory: (token, limit, surface) => {
        const params = new URLSearchParams({ limit: String(limit || 10) });
        if (surface) params.set('surface', surface);
        return apiGet(`/api/library/search-history?${params.toString()}`, token);
      },
      addSearchHistory: (token, entry) => apiPost('/api/library/search-history', entry, token),
      deleteSearchHistoryItem: (token, id) => apiDelete(`/api/library/search-history/${id}`, token),
      clearSearchHistory: (token, surface) => {
        const qs = surface ? `?surface=${encodeURIComponent(surface)}` : '';
        return apiDelete(`/api/library/search-history${qs}`, token);
      },
    },

    // ── DJPal ──
    djpal: {
      getToken: (accessToken) => apiPost('/api/auth/djpal-token', {}, accessToken),
      validateToken: (token) => apiPost('/api/djpal/auth/token/validate', { token }),
      // Server-owned client config (public, pre-auth). Used by the native
      // mix-cover poll (utils/nativeMixCover.js) to read NATIVE_MIX_COVER_MODE
      // without an app release. Maps to music-info GET /v1/config/lazy-resolve.
      getClientConfig: () => apiGet('/api/djpal/config/lazy-resolve'),
      resolveMixesByLinks: (token, links) => apiPost('/api/djpal/mixes/resolve-by-links', { links }, token),
      // Batch catalog lookup by public id. Mirrors the Electron IPC (which
      // returns the bare array via api-client's `json.data ?? json`). The
      // likes list re-hydrates cover/YT/sample fields through this — it was
      // missing here, so web/mobile likes showed the like-time snapshot only
      // (blank cover + no play icon for rows liked without them).
      tracksByPublicIds: (token, publicIds) =>
        apiPost('/api/djpal/tracks/by-public-ids', { publicIds }, token)
          .then(r => (r && r.error) ? r : (r?.data ?? r))
          .catch(e => ({ error: e.message })),
      searchMixes: (token, filters, dateRange, pagination) => apiPost('/api/djpal/mixes/search', { filters, dateRange, ...pagination }, token),
      searchMixesLite: (token, filters, dateRange, pagination) => apiPost('/api/djpal/mixes/search-lite', { filters, dateRange, ...pagination }, token),
      // Mixpedia home rows (mirrors the Electron IPC handlers)
      latestMixesByArtists: (token, artists, page, limit, artistIds) => apiPost('/api/djpal/mixes/latest-by-artists', { artists, page, limit, ...(Array.isArray(artistIds) && artistIds.length ? { artistIds } : {}) }, token).then(r => ({ results: r?.data?.results || r?.results || [], meta: r?.meta || null })).catch(e => ({ error: e.message })),
      latestTracksByArtists: (token, artists, page, limit, seed, artistIds) => apiPost('/api/djpal/mixes/latest-tracks-by-artists', { artists, page, limit, seed, ...(Array.isArray(artistIds) && artistIds.length ? { artistIds } : {}) }, token).then(r => ({ results: r?.data?.results || r?.results || [], meta: r?.meta || null })).catch(e => ({ error: e.message })),
      tracksByArtistsRecentlyPlayed: (token, artists, page, limit, artistIds) => apiPost('/api/djpal/tracks/by-artists-recently-played', { artists, page, limit, ...(Array.isArray(artistIds) && artistIds.length ? { artistIds } : {}) }, token).then(r => ({ results: r?.data?.results || r?.results || [], meta: r?.meta || null })).catch(e => ({ error: e.message })),
      searchArtistTracks: (token, params) => apiPost('/api/djpal/artists/tracks-search', params, token),
      searchTrackMixAppearances: (token, params) => apiPost('/api/djpal/tracks/mix-appearances', params, token),
      getPopularArtists: (token) => apiGet('/api/djpal/artists/popular?limit=100', token),
      // Which of these raw artist names does Mixpedia know? (library follow
      // suggestions) — mirrors the Electron IPC 'djpal:feedResolveArtistsV2'.
      feedResolveArtistsV2: (token, artists) => apiPost('/api/djpal/feed/v2/resolve-artists', { artists }, token),
      autocompleteArtist: (token, q) => apiGet(`/api/djpal/artists/autocomplete?q=${encodeURIComponent(q)}`, token),
      autocompleteLabels: (token, q) => apiGet(`/api/djpal/labels/autocomplete?q=${encodeURIComponent(q)}&limit=8`, token),
      getLabel: (token, publicId, opts = {}) => apiGet(`/api/djpal/labels/${encodeURIComponent(publicId)}?page=${opts.page || 1}&limit=${opts.limit || 50}${opts.summary ? '&summary=1' : ''}`, token),
      getLabelList: (token, publicId, list, opts = {}) => apiGet(`/api/djpal/labels/${encodeURIComponent(publicId)}/${encodeURIComponent(list)}?page=${opts.page || 1}&limit=${opts.limit || 50}${opts.sort ? `&sort=${encodeURIComponent(opts.sort)}` : ''}`, token),
      getLabelArtistTracks: (token, labelPublicId, artistPublicId, opts = {}) => apiGet(`/api/djpal/labels/${encodeURIComponent(labelPublicId)}/artists/${encodeURIComponent(artistPublicId)}/tracks?kind=${encodeURIComponent(opts.kind || 'supporting')}&page=${opts.page || 1}&limit=${opts.limit || 20}`, token),
      autocompleteTrack: (token, q) => apiGet(`/api/djpal/tracks/autocomplete?q=${encodeURIComponent(q)}`, token),
      searchTracks: (token, q, page, limit) => apiGet(`/api/djpal/tracks/search?q=${encodeURIComponent(q)}&page=${page || 1}&limit=${limit || 50}`, token),
      searchTracksPlayedBy: (token, opts = {}) => {
        const params = new URLSearchParams({ page: String(opts.page || 1), limit: String(opts.limit || 50) });
        if (opts.artistPublicId) params.set('artist_id', opts.artistPublicId);
        if (!opts.artistPublicId && opts.artistName) params.set('artist', opts.artistName);
        if (opts.dateRange?.from) params.set('dateFrom', opts.dateRange.from);
        if (opts.dateRange?.to) params.set('dateTo', opts.dateRange.to);
        return apiGet(`/api/djpal/tracks/played-by?${params.toString()}`, token);
      },
      searchCoPlayedTracks: (token, opts = {}) => {
        const params = new URLSearchParams({ page: String(opts.page || 1), limit: String(opts.limit || 50) });
        if (opts.djArtistPublicId) params.set('dj_artist_id', opts.djArtistPublicId);
        else if (opts.djArtistName) params.set('dj_artist', opts.djArtistName);
        if (opts.trackArtistPublicId) params.set('track_artist_id', opts.trackArtistPublicId);
        else if (opts.trackArtistName) params.set('track_artist', opts.trackArtistName);
        if (opts.dateRange?.from) params.set('dateFrom', opts.dateRange.from);
        if (opts.dateRange?.to) params.set('dateTo', opts.dateRange.to);
        return apiGet(`/api/djpal/tracks/co-played?${params.toString()}`, token);
      },
      artistTrackPlays: (token, opts = {}) => {
        const params = new URLSearchParams({ by: opts.by || 'self', sort: opts.sort || 'recent', page: String(opts.page || 1), limit: String(opts.limit || 50) });
        if (opts.dateRange?.from) params.set('dateFrom', opts.dateRange.from);
        if (opts.dateRange?.to) params.set('dateTo', opts.dateRange.to);
        return apiGet(`/api/djpal/artists/${encodeURIComponent(opts.artistPublicId || '')}/track-plays?${params.toString()}`, token);
      },
      artistSupported: (token, opts = {}) => {
        const params = new URLSearchParams({ direction: opts.direction || 'by', sort: opts.sort || 'popularity', page: String(opts.page || 1), limit: String(opts.limit || 50) });
        if (opts.q) params.set('q', opts.q);
        return apiGet(`/api/djpal/artists/${encodeURIComponent(opts.artistPublicId || '')}/supported?${params.toString()}`, token);
      },
      autocompleteUnified: (token, q) => apiGet(`/api/djpal/search/autocomplete?q=${encodeURIComponent(q)}`, token),
      searchTrackRecommendations: (token, track, algorithm, variant, dateRange) => {
        // `track` is "Artist - Title" or { trackName, trackId } — same contract
        // as the desktop IPC handler / @mixgoat/api-client.
        const seed = typeof track === 'string' ? { trackName: track } : (track || {});
        return apiPost('/api/djpal/tracks/recommendations/search', { ...seed, algorithm, variant, dateRange }, token);
      },
      playlistRecommendations: (token, source, algorithm, variant) =>
        apiPost('/api/djpal/tracks/recommendations/playlist', { ...(source || {}), algorithm, variant }, token),
      // Set Planner step. No main process here, so no server-backed step quota:
      // the renderer applies the free-plan rule locally (plannerLimits) when the
      // response carries no `quota` field.
      plannerRecommendations: (token, track, _opts) => {
        const seed = typeof track === 'string' ? { trackName: track } : (track || {});
        return apiPost('/api/djpal/tracks/recommendations/search', { ...seed, algorithm: 'NODE2VEC', variant: 'A' }, token);
      },
      fetchFeed: (token, params) => apiPost('/api/djpal/feed', params, token),
      fetchFeedAiPicks: (token, feedId) => apiPost('/api/djpal/feed/aipicks', { feedId }, token),
      fetchTrending: (token, params) => apiPost('/api/djpal/feed/trending', params || {}, token),
      extractStreamUrl: async (url) => {
        // Native (iOS/Android) is samples-only by design. Playable tracks
        // must already have a sampleUrl/previewUrl on them; we never reach
        // into stream-service or any other full-stream extraction here.
        // Reasons: (1) App Store Review Guideline 5.2.3 forbids playing
        // licensed third-party content without partnership; (2) consistent
        // behavior with the web build at app.mixgoat.com.
        return { url, fallback: true };
      },
      downloadForPeaks: () => Promise.resolve(null),
      onNav: () => () => {},
    },

    // ── Tier — reconciled (Stripe || Apple || Google) ──
    tier: {
      startTrial: (accessToken) => apiPost('/api/subscriptions/start-trial', {}, accessToken),
      getInfo: async (user) => {
        const localTrialInfo = getTrialInfo(user);
        let token = authHeaderToken(user?.accessToken);
        if (token) {
          // Server-side reconciled tier — ORs Stripe with Apple/Google entitlements.
          let reconciled = await apiGet(TIER_RECONCILED_PATH, token);
          // 401 → access token expired. Refresh once and retry. Without this,
          // a stale token (>15 min) makes the reconciled call silently fail and
          // the fallback path below uses the auth.user object's historical
          // trial_started_at/trial_ends_at — which still hold even after the
          // user moved off trial to a paid IAP. Net effect: paying IAP users
          // see "trial expired" banner and lose GOAT features until next sign-in.
          if (reconciled && reconciled.error && reconciled.status === 401) {
            const refreshTok = refreshTokenForBody();
            if (refreshTok) {
              const refreshRes = await apiPost('/api/auth/refresh', { refreshToken: refreshTok });
              if (refreshRes && !refreshRes.error && refreshRes.accessToken) {
                reconciled = await apiGet(TIER_RECONCILED_PATH, refreshRes.accessToken);
              }
            }
          }
          if (reconciled && !reconciled.error) {
            // Build trialInfo from SERVER's flat fields, not the local user
            // object. Local fields can be stale (auth-service has old trial
            // dates the subscription-service has since cleared) — and when
            // the user has an active IAP, the server's authoritative answer
            // is isTrialExpired=false even if their old trial ended months ago.
            const serverTrialInfo = {
              trialStartDate: reconciled.trialStartDate || null,
              trialEndDate: reconciled.trialEndDate || null,
              daysRemaining: reconciled.daysRemaining || 0,
              isTrialActive: !!reconciled.isTrialActive,
              isTrialExpired: !!reconciled.isTrialExpired,
            };
            return { ...reconciled, trialInfo: serverTrialInfo };
          }
          // We HAD a token but the server didn't resolve (stale token right
          // after a cold resume, refresh raced the wake, or the network wasn't
          // ready yet). Do NOT assert 'free' here: an IAP-GOAT user isn't
          // reflected in subscription_type or local trial, so the free-returning
          // fallback below would DOWNGRADE a paying user — the resume
          // flash-to-free. Signal 'unresolved' so the tier thunks keep the
          // cached GOAT instead of overwriting it. A genuine lapse still comes
          // back as a SUCCESSFUL reconciled 'free' (handled above) and applies.
          return { error: (reconciled && reconciled.error) || 'tier_unresolved', unresolved: true };
        }
        // No token at all (genuinely pre-auth / offline) — local best-effort.
        if (user && user.subscription_type === 'goat') {
          return { tier: 'goat', reason: 'subscription', effectiveTier: 'goat', trialInfo: localTrialInfo, ...localTrialInfo };
        }
        if (localTrialInfo.isTrialActive) {
          return { tier: 'goat', reason: 'trial', effectiveTier: 'goat', trialInfo: localTrialInfo, ...localTrialInfo };
        }
        // Can't positively confirm a tier (no token, and the local user object
        // carries no subscription_type — true for IAP-GOAT users). Returning a
        // definitive 'free' here would clobber a cached GOAT (the renderer
        // applies any non-unresolved result). Signal unresolved so the cached
        // tier survives; a successful online reconcile later corrects a genuine
        // lapse.
        return { error: 'tier_unresolved', unresolved: true };
      },
      canAccess: async (featureKey, user) => {
        const info = await window.electron.tier.getInfo(user);
        const isGoat = info.effectiveTier === 'goat';
        return { allowed: isGoat, tier: isGoat ? 'goat' : 'free', featureLabel: featureKey };
      },
      getTrialInfo: (user) => Promise.resolve(getTrialInfo(user)),
    },

    // ── Subscription (Stripe surface — kept for parity; not invoked on native) ──
    subscription: {
      getCurrent: (token) => apiGet('/api/subscriptions/current', token),
      getPricing: () => apiGet('/api/subscriptions/pricing'),
      changePlan: (token, plan) => apiPost('/api/subscriptions/change-plan', { plan }, token),
      cancel: (token) => apiPost('/api/subscriptions/cancel', {}, token),
      reactivate: (token) => apiPost('/api/subscriptions/reactivate', {}, token),
      createSetupIntent: (token) => apiPost('/api/subscriptions/setup-intent', { mode: 'update-card', allowRedirects: false }, token),
      createSubscription: (token, plan, paymentMethodId) =>
        apiPost('/api/subscriptions/create', { plan, paymentMethodId }, token),
      updatePaymentMethod: (token, paymentMethodId) =>
        apiPost('/api/subscriptions/update-payment-method', { paymentMethodId }, token),
    },

    // ── IAP (RevenueCat) — native-only addition. MobilePaywall.jsx uses this. ──
    iap,

    // ── Native theme bridge (iOS keyboard appearance) ──
    // The MixGoatTheme Capacitor plugin (Swift) sets
    // overrideUserInterfaceStyle on the WebView host. This is the only
    // reliable way to make the iOS on-screen keyboard match the in-app
    // theme — CSS `color-scheme` alone does NOT work for the keyboard
    // popup in WKWebView. App.js calls setStyle whenever the resolved
    // theme changes. On web/Electron, window.electron.theme is undefined
    // and the call is a no-op via optional chaining.
    theme: {
      setStyle: async (style) => {
        try {
          const plugin = window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins.MixGoatTheme;
          if (!plugin) return { error: 'MixGoatTheme plugin unavailable' };
          // Normalize to the three values the Swift side understands.
          const normalized = style === 'light' ? 'light' : (style === 'dark' ? 'dark' : 'system');
          return await plugin.setStyle({ style: normalized });
        } catch (err) {
          return { error: err?.message || 'setStyle failed' };
        }
      },
    },

    // ── Push notifications (APNs / FCM via @capacitor/push-notifications) ──
    // Three-step handshake driven by src/renderer/App.js after the user is
    // authenticated:
    //   1. requestPermission() → fires the iOS system prompt (only the first
    //      time; subsequent calls return the cached granted/denied status).
    //   2. register() → calls native registerForRemoteNotifications. Triggers
    //      the JS 'registration' event once APNs hands us a device token.
    //   3. getToken() → resolves the token from the 'registration' event.
    //      Cached so callers don't have to re-listen.
    //
    // ALL of this is native-only; the entire shim returns early when
    // window.Capacitor.isNativePlatform() is false (see top of file), so
    // these methods literally don't exist on web/Electron. The App.js call
    // site gates the entire effect on `isNative` for extra safety.
    push: (() => {
      let cachedToken = null;
      let tokenWaiters = [];
      let listenersInstalled = false;

      function installListeners() {
        if (listenersInstalled) return;
        const { PushNotifications } = window.Capacitor.Plugins;
        if (!PushNotifications) return;
        listenersInstalled = true;
        PushNotifications.addListener('registration', (token) => {
          cachedToken = token && token.value ? token.value : null;
          const waiters = tokenWaiters;
          tokenWaiters = [];
          waiters.forEach((resolve) => resolve(cachedToken));
        });
        PushNotifications.addListener('registrationError', (err) => {
          console.warn('[capacitor-shim] push registrationError', err);
          const waiters = tokenWaiters;
          tokenWaiters = [];
          waiters.forEach((resolve) => resolve(null));
        });
        // pushNotificationReceived / pushNotificationActionPerformed are
        // surfaced as window events so the in-app notification panel can
        // refresh its unread count when a push arrives while the app is
        // foregrounded. Wiring the UI side of this is out of scope for
        // the push-delivery slice; the listeners are installed so we
        // don't drop these events when we add UI later.
        PushNotifications.addListener('pushNotificationReceived', (notif) => {
          try {
            window.dispatchEvent(new CustomEvent('mixgoat:push-received', { detail: notif }));
          } catch (_) {}
        });
        PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
          try {
            window.dispatchEvent(new CustomEvent('mixgoat:push-action', { detail: action }));
          } catch (_) {}
        });
      }

      return {
        async requestPermission() {
          try {
            const { PushNotifications } = window.Capacitor.Plugins;
            if (!PushNotifications) return { granted: false, reason: 'plugin-unavailable' };
            const res = await PushNotifications.requestPermissions();
            return { granted: res && res.receive === 'granted', raw: res };
          } catch (err) {
            return { granted: false, error: err?.message || 'requestPermission failed' };
          }
        },
        async register() {
          try {
            const { PushNotifications } = window.Capacitor.Plugins;
            if (!PushNotifications) return { success: false, error: 'plugin-unavailable' };
            installListeners();
            await PushNotifications.register();
            return { success: true };
          } catch (err) {
            return { success: false, error: err?.message || 'register failed' };
          }
        },
        async getToken({ timeoutMs = 10000 } = {}) {
          if (cachedToken) return cachedToken;
          installListeners();
          return new Promise((resolve) => {
            const timer = setTimeout(() => {
              tokenWaiters = tokenWaiters.filter((w) => w !== resolve);
              resolve(null);
            }, timeoutMs);
            tokenWaiters.push((token) => {
              clearTimeout(timer);
              resolve(token);
            });
          });
        },
        // Clear every notification this app has delivered to the system tray.
        // On Android this is what removes the launcher "notification dot" once
        // the user has read/cleared notifications in-app; on iOS it clears the
        // Notification Center entries.
        async removeAllDelivered() {
          try {
            const { PushNotifications } = window.Capacitor.Plugins;
            if (!PushNotifications) return { success: false };
            await PushNotifications.removeAllDeliveredNotifications();
            return { success: true };
          } catch (err) {
            return { success: false, error: err?.message || 'removeAllDelivered failed' };
          }
        },
      };
    })(),

    // ── App-icon badge (@capawesome/capacitor-badge) ──
    // Sets/clears the numeric badge on the app icon. iOS renders the red
    // number; Android support varies by launcher (the count is best-effort —
    // the notification dot, cleared via push.removeAllDelivered(), is the
    // reliable Android indicator). Native-only; the whole shim no-ops on web.
    badge: {
      async set(count) {
        try {
          const { Badge } = window.Capacitor.Plugins;
          if (!Badge) return { success: false };
          const n = Math.max(0, parseInt(count, 10) || 0);
          if (n <= 0) await Badge.clear();
          else await Badge.set({ count: n });
          return { success: true };
        } catch (err) {
          return { success: false, error: err?.message || 'badge.set failed' };
        }
      },
      async clear() {
        try {
          const { Badge } = window.Capacitor.Plugins;
          if (!Badge) return { success: false };
          await Badge.clear();
          return { success: true };
        } catch (err) {
          return { success: false, error: err?.message || 'badge.clear failed' };
        }
      },
    },

    openUrl: async (url) => {
      try {
        if (typeof url === 'string' && url.startsWith('http')) {
          await window.Capacitor.Plugins.Browser.open({ url });
        }
      } catch (_) {}
      return { success: true };
    },
    onPaymentSuccess: () => () => {},

    // ── App version (About page) ── build-time constant, see web-electron-shim.
    version: {
      get: () => Promise.resolve(process.env.REACT_APP_VERSION || null),
    },

    // ── Analytics (posthog-js in prod via window.__mixgoatPh, see
    //    src/analyticsClient.js; console.debug always) ──
    analytics: {
      track: (event, properties) => { console.debug('[analytics]', event, properties); window.__mixgoatPh?.phTrack(nativePlatform(), event, properties); return Promise.resolve(); },
      identify: (userId, properties) => { console.debug('[analytics:identify]', userId, properties); window.__mixgoatPh?.phIdentify(nativePlatform(), userId, properties); return Promise.resolve(); },
      setPersonProperties: (properties) => { console.debug('[analytics:setPersonProperties]', properties); window.__mixgoatPh?.phSetPersonProperties(properties); return Promise.resolve(); },
      reset: () => { console.debug('[analytics:reset]'); window.__mixgoatPh?.phReset(); return Promise.resolve(); },
    },

    // ── Store (Capacitor Preferences) ──
    store: {
      get(key) {
        const val = memStore[`store:${key}`];
        if (val === undefined || val === null) return undefined;
        try { return JSON.parse(val); } catch { return undefined; }
      },
      set(key, val) {
        prefsSet(`store:${key}`, JSON.stringify(val));
      },
    },

    // ── Desktop-only stubs (file I/O, dialogs) — same no-ops as web shim ──
    getAudioUrl: () => Promise.resolve(null),
    getAlbumArtUrl: () => Promise.resolve(null),
    appReady: () => {},
    shell: { showItemInFolder: () => Promise.resolve(), openPath: () => Promise.resolve() },
    dialog: { showOpenDialog: () => Promise.resolve({ canceled: true }), showSaveDialog: () => Promise.resolve({ canceled: true }) },
    saveBuffer: () => Promise.resolve(),
    clipboard: { writeImage: () => Promise.resolve({ success: false, error: 'Not supported on native' }) },
    getPathForFile: () => '',
    openFile: () => Promise.resolve(null),
    openDir: () => Promise.resolve(null),
    resetFilePath: () => Promise.resolve(),
    cancelFixing: () => Promise.resolve(),
    cancelLoudnessMatching: () => Promise.resolve(),
    cancelOrganizing: () => Promise.resolve(),
    cancelAnalyzeInfo: () => Promise.resolve(),
    clearAnalysisCache: () => Promise.resolve(),
    cancelFixMetadata: () => Promise.resolve(),
    fixAllFilesInDir: () => {},
    matchAllFilesInDir: () => {},
    analyzeLibrary: () => {},
    organizeTracks: () => {},
    appendFileNames: () => {},
    analyzeMetadata: () => {},
    writeMetadata: () => {},

    // Marker: web-electron-shim.js's idempotency guard checks for `__native`
    // (in addition to `db`) and skips when it sees this — preventing the
    // web shim from overwriting our native one. We INTENTIONALLY do NOT set
    // a `db` sentinel here: leaving `db` undefined means every existing
    // `!window.electron.db` check (used across 20+ files as a "web vs
    // Electron" discriminator) evaluates to TRUE on native, so the native
    // runtime automatically takes the web-style code paths (mobile layout,
    // no DJ-migration sidebars, HTTP shim). isNative is the additional
    // signal for places that need to differentiate native from browser.
    __native: true,
  };

  // Restore cached tokens on load. After hydration, if Keychain has
  // tokens but localStorage doesn't (iOS evicted WKWebView storage on
  // low memory), mirror them back to localStorage and tell the app to
  // re-run its auth check so the user stops seeing the login screen.
  hydratePrefs().then(() => {
    cachedAccessToken = memStore[LS_ACCESS] || null;
    cachedRefreshToken = memStore[LS_REFRESH] || null;
    const accessFromKeychain = memStore[LS_ACCESS];
    const refreshFromKeychain = memStore[LS_REFRESH];
    const userFromKeychain = memStore[LS_USER];
    let recovered = false;
    try {
      if (accessFromKeychain && !localStorage.getItem(WEB_LS_ACCESS)) {
        localStorage.setItem(WEB_LS_ACCESS, accessFromKeychain);
        recovered = true;
      }
      if (refreshFromKeychain && !localStorage.getItem(WEB_LS_REFRESH)) {
        localStorage.setItem(WEB_LS_REFRESH, refreshFromKeychain);
        recovered = true;
      }
      if (userFromKeychain && !localStorage.getItem(WEB_LS_USER)) {
        localStorage.setItem(WEB_LS_USER, userFromKeychain);
        recovered = true;
      }
    } catch {}
    if (recovered) {
      window.dispatchEvent(new CustomEvent('capacitor-auth-rehydrated'));
    }
  });

  // Hide the iOS keyboard's accessory bar (the < > ✓ form-navigation
  // bubble above the keyboard). No web-side attribute removes it —
  // requires the native @capacitor/keyboard plugin call. Fire-and-forget;
  // safe to skip if the plugin isn't available.
  try {
    const { Keyboard } = window.Capacitor.Plugins;
    if (Keyboard && typeof Keyboard.setAccessoryBarVisible === 'function') {
      Keyboard.setAccessoryBarVisible({ isVisible: false }).catch(() => {});
    }
  } catch (_) {}

  // Content deep links (Universal/App Links + mixgoat://open?path=…). A shared
  // mixgoat.com/track|mix|artist/… link opens the app here; we normalise it to
  // the website path and hand it to the renderer's deep-link navigator
  // (App.js → window.__mixgoatApplyDeepLink). OAuth callbacks (mixgoat://auth/
  // callback) are handled by the transient listeners in the login flows, so we
  // skip them. Also checks the cold-start launch URL.
  try {
    const { App } = window.Capacitor.Plugins;
    const toDeepLinkPath = (url) => {
      try {
        const u = new URL(url);
        if (u.protocol === 'https:') return u.pathname + (u.search || '');
        if (u.protocol === 'mixgoat:') {
          const viaParam = u.searchParams.get('path');
          if (viaParam) return viaParam;
          if (['track', 'mix', 'artist'].includes(u.host)) return `/${u.host}${u.pathname}${u.search || ''}`;
        }
      } catch (_) {}
      return null;
    };
    const routeUrl = (url) => {
      if (!url || (typeof url === 'string' && url.startsWith('mixgoat://auth/callback'))) return;
      const path = toDeepLinkPath(url);
      if (path && /^\/(track|mix|artist)\//.test(path)) {
        // Renderer may not have registered the hook yet on a cold launch —
        // retry briefly until App.js mounts.
        let tries = 0;
        const tryApply = () => {
          if (typeof window.__mixgoatApplyDeepLink === 'function') window.__mixgoatApplyDeepLink(path);
          else if (tries++ < 40) setTimeout(tryApply, 150);
        };
        tryApply();
      }
    };
    if (App && typeof App.addListener === 'function') {
      App.addListener('appUrlOpen', (event) => routeUrl(event && event.url));
    }
    if (App && typeof App.getLaunchUrl === 'function') {
      App.getLaunchUrl().then((res) => { if (res && res.url) routeUrl(res.url); }).catch(() => {});
    }
  } catch (_) {}
})();
